import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, ChevronDown, FolderOpen, MoreHorizontal, Plus } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Collection, CollectionStatus, CollectionVisibility } from '../types';
import { CollectionsService } from '@/services/collections-service';
import { useState } from 'react';
import { toast } from 'sonner';

const statusColors: Record<CollectionStatus, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
};

const statusLabels: Record<CollectionStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

const visibilityColors: Record<CollectionVisibility, string> = {
  public: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  private: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  restricted: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
};

const visibilityLabels: Record<CollectionVisibility, string> = {
  public: 'Public',
  private: 'Private',
  restricted: 'Restricted',
};

interface CollectionTableProps {
  data: Collection[];
  loading?: boolean;
  onRefresh?: () => void;
}

export function CollectionTable({ data, loading = false, onRefresh }: CollectionTableProps) {
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const columns: ColumnDef<Collection>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      header: 'Collection',
      cell: ({ row }) => {
        const collection = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center">
              <FolderOpen className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="font-medium">{collection.name}</span>
              <span className="text-muted-foreground text-sm truncate max-w-[300px]">
                {collection.description || 'No description'}
              </span>
            </div>
          </div>
        );
      },
      size: 300,
    },
    {
      accessorKey: 'document_count',
      header: 'Documents',
      cell: ({ row }) => {
        const count = row.getValue('document_count') as number;
        return <div className="text-center">{count}</div>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as CollectionStatus;
        return (
          <Badge className={statusColors[status]} variant="outline">
            {statusLabels[status]}
          </Badge>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: 'visibility',
      header: 'Visibility',
      cell: ({ row }) => {
        const visibility = row.getValue('visibility') as CollectionVisibility;
        return (
          <Badge className={visibilityColors[visibility]} variant="outline">
            {visibilityLabels[visibility]}
          </Badge>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Created
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const timestamp = row.getValue('created_at') as number;
        const date = new Date(timestamp * 1000); // Convert Unix timestamp to JS Date
        return (
          <div className="text-sm" title={format(date, 'PPpp')}>
            {formatDistanceToNow(date, { addSuffix: true })}
          </div>
        );
      },
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      cell: ({ row }) => {
        const timestamp = row.getValue('updated_at') as number;
        const date = new Date(timestamp * 1000); // Convert Unix timestamp to JS Date
        return (
          <div className="text-sm" title={format(date, 'PPpp')}>
            {formatDistanceToNow(date, { addSuffix: true })}
          </div>
        );
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const collection = row.original;

        const handleToggleVisibility = async () => {
          try {
            setActionLoading(collection.id);
            await CollectionsService.toggleCollectionVisibility(collection.id);
            onRefresh?.();
          } catch (error) {
            console.error('Error toggling visibility:', error);
          } finally {
            setActionLoading(null);
          }
        };

        const handleDelete = async () => {
          if (window.confirm(`Are you sure you want to delete "${collection.name}"? This action cannot be undone.`)) {
            try {
              setActionLoading(collection.id);
              await CollectionsService.deleteCollection(collection.id);
              onRefresh?.();
            } catch (error) {
              console.error('Error deleting collection:', error);
            } finally {
              setActionLoading(null);
            }
          }
        };

        const isLoading = actionLoading === collection.id;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0" disabled={isLoading}>
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/collections/$id" params={{ id: collection.id }}>View details</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/collections/edit/$id" params={{ id: collection.id }}>Edit collection</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleToggleVisibility} disabled={isLoading}>
                {collection.visibility === 'public' ? 'Make private' : 'Make public'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={handleDelete} disabled={isLoading}>
                Delete collection
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full">
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Filter collections..."
            value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
            onChange={(event) =>
              table.getColumn('name')?.setFilterValue(event.target.value)
            }
            className="max-w-sm"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Status <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(statusLabels).map(([status, label]) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={table
                    .getColumn('status')
                    ?.getFilterValue() as string[] | undefined
                    ? (table.getColumn('status')?.getFilterValue() as string[]).includes(status)
                    : true
                  }
                  onCheckedChange={(value) => {
                    const filterValues = (table.getColumn('status')?.getFilterValue() as string[]) || [];
                    if (value) {
                      table.getColumn('status')?.setFilterValue([...filterValues, status]);
                    } else {
                      table.getColumn('status')?.setFilterValue(filterValues.filter((val) => val !== status));
                    }
                  }}
                >
                  {label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Visibility <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(visibilityLabels).map(([visibility, label]) => (
                <DropdownMenuCheckboxItem
                  key={visibility}
                  checked={table
                    .getColumn('visibility')
                    ?.getFilterValue() as string[] | undefined
                    ? (table.getColumn('visibility')?.getFilterValue() as string[]).includes(visibility)
                    : true
                  }
                  onCheckedChange={(value) => {
                    const filterValues = (table.getColumn('visibility')?.getFilterValue() as string[]) || [];
                    if (value) {
                      table.getColumn('visibility')?.setFilterValue([...filterValues, visibility]);
                    } else {
                      table.getColumn('visibility')?.setFilterValue(filterValues.filter((val) => val !== visibility));
                    }
                  }}
                >
                  {label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="ml-auto">
            Export
          </Button>
          <Button asChild size="sm">
            <Link to="/collections/add">
              <Plus className="mr-2 h-4 w-4" />
              Add Collection
            </Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No collections found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{' '}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
